import { Inject, Injectable, OnDestroy, DOCUMENT } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { datadogRum } from '@datadog/browser-rum';
import { getRumModuleFromViewName, getRumViewName } from './rum-view-name.util';

type RumActionName = 'Approve' | 'Skip' | 'Reject' | 'Send Back' | 'Save' | 'Submit' | 'Export';

@Injectable({ providedIn: 'root' })
export class RumActionTrackerService implements OnDestroy {
	private currentViewName: string = 'unknown';
	private currentModule: string = 'unknown';
	private teardownFns: Array<() => void> = [];

	constructor(
		private router: Router,
		private activatedRoute: ActivatedRoute,
		@Inject(DOCUMENT) private document: Document
	) {}

	init(): void {
		// Make init idempotent.
		if (this.teardownFns.length) return;

		const routerEventsSubscription = this.router.events.subscribe((event) => {
			if (event instanceof NavigationEnd) {
				const url = event.urlAfterRedirects ?? event.url ?? '';
				const explicitViewName = this.getRouteViewName(this.activatedRoute);
				this.currentViewName = explicitViewName || getRumViewName(url);
				this.currentModule = getRumModuleFromViewName(this.currentViewName);
			}
		});
		this.teardownFns.push(() => routerEventsSubscription.unsubscribe());

		const clickHandler = (e: Event) => {
			const target = e.target as Element | null;
			if (!target) return;

			const actionable = target.closest('button, a, input, [role="button"]') as Element | null;
			if (!actionable) return;

			const action = this.detectActionFromElement(actionable);
			if (!action) return;

			// Prevent rapid double-fire for same element (e.g. click + key events).
			const now = Date.now();
			const last = Number((actionable as HTMLElement).dataset?.['rumActionTs'] || 0);
			if (now - last < 750) return;
			(actionable as HTMLElement).dataset['rumActionTs'] = String(now);

			this.trackAction(action, { formType: this.currentViewName });
		};

		const submitHandler = (e: Event) => {
			const form = e.target as HTMLFormElement | null;
			if (!form || form.tagName !== 'FORM') return;
			const submitAction = this.detectSubmitAction(e);
			if (!submitAction) return;
			this.trackAction(submitAction, { formType: this.currentViewName });
		};

		this.document.addEventListener('click', clickHandler, true);
		this.document.addEventListener('submit', submitHandler, true);

		this.teardownFns.push(() => this.document.removeEventListener('click', clickHandler, true));
		this.teardownFns.push(() => this.document.removeEventListener('submit', submitHandler, true));
	}

	ngOnDestroy(): void {
		for (const fn of this.teardownFns.splice(0)) {
			try {
				fn();
			} catch {
				// ignore
			}
		}
	}

	public trackAction(actionName: RumActionName, extra: Record<string, unknown> = {}): void {
		try {
			datadogRum.addAction(actionName, {
				module: this.currentModule,
				viewName: this.currentViewName,
				...extra,
			});
		} catch {
			// If RUM isn't initialized (e.g. missing token / local dev), no-op.
		}
	}

	public getCurrentViewName(): string {
		return this.currentViewName;
	}

	private detectActionFromElement(el: Element): RumActionName | null {
		const text = (el.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
		const ariaLabel = (el.getAttribute('aria-label') || '').replace(/\s+/g, ' ').trim().toLowerCase();
		const titleAttr = (el.getAttribute('title') || '').replace(/\s+/g, ' ').trim().toLowerCase();
		const combined = `${text} ${ariaLabel} ${titleAttr}`.trim();

		// Export patterns: “Export”, “Export to Excel”, “Export to PDF”.
		if (combined === 'export' || combined.includes('export')) {
			return 'Export';
		}

		return null;
	}

	private detectSubmitAction(e: Event): Extract<RumActionName, 'Save' | 'Submit'> | null {
		const submitEvent = e as SubmitEvent;
		const submitter = submitEvent.submitter as Element | null;
		if (!submitter) return null;

		const text = (submitter.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
		const ariaLabel = (submitter.getAttribute('aria-label') || '').replace(/\s+/g, ' ').trim().toLowerCase();
		const titleAttr = (submitter.getAttribute('title') || '').replace(/\s+/g, ' ').trim().toLowerCase();
		const valueAttr = (submitter.getAttribute('value') || '').replace(/\s+/g, ' ').trim().toLowerCase();
		const combined = `${text} ${ariaLabel} ${titleAttr} ${valueAttr}`.trim();

		if (combined.includes('save')) return 'Save';
		if (combined.includes('submit')) return 'Submit';
		return null;
	}

	private getRouteViewName(route: ActivatedRoute): string | undefined {
		let current: ActivatedRoute | null = route;
		while (current?.firstChild) {
			current = current.firstChild;
		}
		return current?.snapshot?.data?.['viewName'] as string | undefined;
	}

	// NOTE: RUM view-name + module normalization lives in rum-view-name.util.ts.
}
